import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Loader2, Eye, EyeOff, Factory, Shield, Cog, MapPin } from "lucide-react";

// Use window.location.origin to ensure API calls go to the same domain
const API = typeof window !== 'undefined' ? `${window.location.origin}/api` : '/api';
const LOGO_URL = "https://customer-assets.emergentagent.com/job_eurokon-move/artifacts/slgvzlik_logo.png";

// Background image from Eurokon website
const BG_IMAGE = "https://eurokonglobal.com/wp-content/uploads/2022/09/E_48.jpg";

export default function Login({ onLogin }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [appVersion, setAppVersion] = useState("");
  const [location, setLocation] = useState({ latitude: null, longitude: null });
  const [locationStatus, setLocationStatus] = useState("pending"); // pending, granted, denied, unavailable

  useEffect(() => {
    // Fetch app version on mount
    axios.get(`${API}/version`)
      .then(res => setAppVersion(res.data.version || ""))
      .catch(() => setAppVersion(""));
    
    // Request location permission
    if ("geolocation" in navigator) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setLocation({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude
          });
          setLocationStatus("granted");
        },
        (error) => {
          console.log("Geolocation error:", error.message);
          if (error.code === error.PERMISSION_DENIED) {
            setLocationStatus("denied");
          } else {
            setLocationStatus("unavailable");
          }
        },
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
      );
    } else {
      setLocationStatus("unavailable");
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!username || !password) {
      toast.error("Please enter username and password");
      return;
    }

    setLoading(true);
    // Wrap the POST in a tiny retry helper. The deployed backend
    // occasionally returns Cloudflare 520 / 502 / 504 when the pod is
    // rolling. A single quick retry hides that from the user, who would
    // otherwise see "Login failed" and have to re-enter credentials.
    const TRANSIENT_STATUSES = new Set([502, 503, 504, 520, 521, 522, 524]);
    const postLogin = () => axios.post(`${API}/auth/login`, {
      username,
      password,
      latitude: location.latitude,
      longitude: location.longitude,
      screen_width: window.screen.width,
      screen_height: window.screen.height,
    });

    try {
      let response;
      try {
        response = await postLogin();
      } catch (err) {
        const st = err.response?.status;
        const transient =
          (st && TRANSIENT_STATUSES.has(st)) ||
          err.message === "Network Error" ||
          err.code === "ERR_NETWORK";
        if (!transient) throw err;
        // 800 ms breather lets a rolling pod settle, then try once more.
        await new Promise(r => setTimeout(r, 800));
        response = await postLogin();
      }
      
      const { access_token, user } = response.data;
      
      // Store token and user
      localStorage.setItem("token", access_token);
      localStorage.setItem("user", JSON.stringify(user));
      
      // Set default auth header
      axios.defaults.headers.common["Authorization"] = `Bearer ${access_token}`;
      
      toast.success(`Welcome, ${user.full_name || user.username}!`);
      onLogin(user);
    } catch (error) {
      console.error("Login error:", error);
      console.error("Response data:", error.response?.data);
      console.error("Response status:", error.response?.status);
      
      let message = "Login failed";
      if (error.response?.data?.detail) {
        message = error.response.data.detail;
      } else if (error.response?.status === 401) {
        message = "Invalid username or password";
      } else if (error.response?.status === 403) {
        message = "Access denied - location restriction";
      } else if (error.response?.status && [502, 503, 504, 520, 521, 522, 524].includes(error.response.status)) {
        message = "Server is restarting — please try again in a few seconds.";
      } else if (error.message === "Network Error") {
        message = "Network error - please check your connection";
      } else if (error.code === "ERR_NETWORK") {
        message = "Unable to connect to server";
      }
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen relative flex" data-testid="login-page">
      {/* Background with industrial image */}
      <div 
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{ 
          backgroundImage: `url(${BG_IMAGE})`,
        }}
      >
        {/* Dark overlay with gradient */}
        <div className="absolute inset-0 bg-gradient-to-br from-zinc-900/95 via-zinc-800/90 to-amber-900/80" />
        
        {/* Subtle pattern overlay */}
        <div 
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}
        />
      </div>

      {/* Left side - Branding */}
      <div className="hidden lg:flex lg:w-1/2 relative z-10 flex-col justify-between p-12">
        <div>
          <img 
            src={LOGO_URL} 
            alt="EUROKON Logo" 
            className="h-16 w-auto object-contain filter brightness-0 invert"
          />
        </div>
        
        <div className="space-y-8">
          <div>
            <h1 className="text-5xl font-black text-white tracking-tight leading-tight">
              Build Your World<br />
              <span className="text-amber-400">of Dreams</span>
            </h1>
            <p className="text-zinc-300 mt-4 text-lg max-w-md">
              Meeting customers' construction needs with a full product range of connectors, fittings, supports and customized products.
            </p>
          </div>
          
          {/* Stats */}
          <div className="grid grid-cols-3 gap-6">
            <div className="border-l-2 border-amber-500 pl-4">
              <p className="text-3xl font-bold text-white">20+</p>
              <p className="text-sm text-zinc-400">Years Experience</p>
            </div>
            <div className="border-l-2 border-amber-500 pl-4">
              <p className="text-3xl font-bold text-white">2500+</p>
              <p className="text-sm text-zinc-400">Products</p>
            </div>
            <div className="border-l-2 border-amber-500 pl-4">
              <p className="text-3xl font-bold text-white">250+</p>
              <p className="text-sm text-zinc-400">Team Members</p>
            </div>
          </div>
        </div>
        
        <div className="flex items-center gap-8 text-zinc-400 text-sm">
          <div className="flex items-center gap-2">
            <Factory className="w-4 h-4" />
            <span>4000+ sqm facility</span>
          </div>
          <div className="flex items-center gap-2">
            <Shield className="w-4 h-4" />
            <span>ISO Certified</span>
          </div>
          <div className="flex items-center gap-2">
            <Cog className="w-4 h-4" />
            <span>State-of-art Technology</span>
          </div>
        </div>
      </div>

      {/* Right side - Login Form */}
      <div className="w-full lg:w-1/2 relative z-10 flex items-center justify-center p-8">
        <Card className="w-full max-w-md border-0 shadow-2xl bg-white/95 backdrop-blur-sm">
          <CardHeader className="text-center pb-2">
            {/* Mobile logo */}
            <div className="flex justify-center lg:hidden mb-6">
              <img 
                src={LOGO_URL} 
                alt="EUROKON Logo" 
                className="h-16 w-auto object-contain"
                data-testid="login-logo"
              />
            </div>
            
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-amber-500 to-amber-600 flex items-center justify-center">
                <Cog className="w-5 h-5 text-white" />
              </div>
              <div className="text-left">
                <CardTitle className="font-heading font-black text-xl uppercase tracking-tight">
                  Production Portal
                </CardTitle>
                <CardDescription className="font-mono text-xs text-zinc-500">
                  Internal Management System
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          
          <CardContent className="pt-6">
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <Label className="text-xs font-semibold text-zinc-600 uppercase tracking-wide">Username</Label>
                <Input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="Enter username"
                  className="mt-1.5 h-12 font-mono bg-zinc-50 border-zinc-200 focus:border-amber-500 focus:ring-amber-500"
                  data-testid="login-username"
                  autoComplete="username"
                />
              </div>
              
              <div>
                <Label className="text-xs font-semibold text-zinc-600 uppercase tracking-wide">Password</Label>
                <div className="relative mt-1.5">
                  <Input
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter password"
                    className="h-12 font-mono bg-zinc-50 border-zinc-200 pr-12 focus:border-amber-500 focus:ring-amber-500"
                    data-testid="login-password"
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600 transition-colors"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>
              
              <Button
                type="submit"
                disabled={loading}
                className="w-full h-12 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white font-bold uppercase tracking-wide shadow-lg shadow-amber-500/25 transition-all"
                data-testid="login-submit"
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin mr-2" />
                ) : null}
                {loading ? "Signing in..." : "Sign In"}
              </Button>
            </form>
            
            <div className="mt-6 pt-6 border-t border-zinc-100">
              {/* Location Status Indicator */}
              <div className="flex items-center justify-center gap-2 mb-3">
                <MapPin className={`w-4 h-4 ${
                  locationStatus === "granted" ? "text-green-500" : 
                  locationStatus === "denied" ? "text-red-500" : 
                  "text-zinc-400"
                }`} />
                <span className={`text-xs ${
                  locationStatus === "granted" ? "text-green-600" : 
                  locationStatus === "denied" ? "text-red-600" : 
                  "text-zinc-500"
                }`}>
                  {locationStatus === "granted" && "Location access granted"}
                  {locationStatus === "denied" && "Location access denied"}
                  {locationStatus === "pending" && "Requesting location..."}
                  {locationStatus === "unavailable" && "Location unavailable"}
                </span>
              </div>
              
              <p className="text-center text-xs text-zinc-400">
                Authorized personnel only. All activities are monitored.
              </p>
              {appVersion && (
                <p className="text-center text-[10px] text-zinc-300 mt-2 font-mono">
                  v{appVersion}
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
      
      {/* Mobile background gradient adjustment */}
      <style>{`
        @media (max-width: 1023px) {
          .min-h-screen > div:first-child::after {
            content: '';
            position: absolute;
            inset: 0;
            background: linear-gradient(to bottom, rgba(0,0,0,0.7), rgba(0,0,0,0.9));
          }
        }
      `}</style>
    </div>
  );
}
